import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { CmdLoader } from '@commander-services/gui-components';
import { useIntl } from 'react-intl';
import { useQueries } from '@tanstack/react-query';
import { BluecoinUsageApiResponse } from '../interfaces';
import Export from './Export';
import PermissionsService, { PERMISSION_BLUECOINS } from '../../../Services/PermissionsService';
import TabComponent from '../../TabComponent';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import BluecoinsSettingsSchema from '../BluecoinsSettingsSchema.json';
import TableDatepicker from '../../TableDatepicker';
import { IDatepickerSettings, setTableDatepickerAtom } from '../../../store/recoil/TableState';
import { getDayjsLocale, getDefaultLanguage } from '../../../Services/LanguageService';
import { CmdTable } from '../../CmdTable/CmdTable';
import {
  BLE_USAGE_TABLE_NAME,
  BLE_USAGE_TABLE_SETTINGS_KEY,
  BlucoinsUsageTableTranslationKeys,
  CUSTOMER_KEY,
  USAGE_END_ADDRESS_KEY,
  USAGE_START_ADDRESS_KEY,
  VEHICLE_KEY,
} from '../constants';
import useBleUsage from './useBleUsage';
import { BluecoinUsageRecord } from './BluecoinsUsageState';
import CmdTableSettings from '../../CmdTable/settings/CmdTableSettings';
import { customersAtom, ICustomer } from '../../../store/recoil/customers';
import FilterChips from '../../CmdTable/FilterChips/FilterChips';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import { getTableSettings, handleOrderColumns } from '../../CmdTable/CmdTableService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  tableFiltersAtomFamily,
} from '../../CmdTable/CmdTableState';

function BluecoinsUsage(): JSX.Element {
  const { formatMessage: f } = useIntl();
  dayjs.extend(weekOfYear);
  const [filterForRequest, setFilterForRequest] = useRecoilState<ICmdTableFilter>(
    filterForRequestAtomFamily(BLE_USAGE_TABLE_NAME)
  );
  const tableFiltersKey = useRecoilValue<string[]>(tableFiltersAtomFamily(BLE_USAGE_TABLE_NAME));

  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const documentTitle = `${f({ id: 'table_list.tabs.bluecoin.usage' })} - ${f({
    id: 'menu.bluecoins',
  })}`;
  useDocumentTitle(documentTitle);
  const [datepickerSettings, setDatepickerSettings] = useRecoilState<IDatepickerSettings | null>(
    setTableDatepickerAtom
  );
  const [minDatepickerTimestamp, setMinDatepickerTimestamp] = React.useState<string>('');
  const [maxDatepickerTimestamp, setMaxDatepickerTimestamp] = React.useState<string>('');
  const { columns, columnOrder, columnVisibility } = useBleUsage();

  React.useEffect(() => {
    if (
      datepickerSettings &&
      datepickerSettings.timestamp &&
      datepickerSettings.timestamp.date &&
      datepickerSettings.timestamp.date.length === 2
    ) {
      const newMinTimestamp = datepickerSettings.timestamp.date[0];
      const newMaxTimestamp = datepickerSettings.timestamp.date[1];

      const isMinDateChanged =
        dayjs(newMinTimestamp).format('DD.MM.YYYY HH:mm:ss:SSS') !==
        dayjs(minDatepickerTimestamp).format('DD.MM.YYYY HH:mm:ss:SSS');
      const isMaxDateChanged =
        dayjs(newMaxTimestamp).format('DD.MM.YYYY HH:mm:ss:SSS') !==
        dayjs(maxDatepickerTimestamp).format('DD.MM.YYYY HH:mm:ss:SSS');

      if (isMinDateChanged) {
        setMinDatepickerTimestamp(newMinTimestamp);
      }

      if (isMaxDateChanged) {
        setMaxDatepickerTimestamp(newMaxTimestamp);
      }

      if (isMinDateChanged || isMaxDateChanged) {
        setFilterForRequest((prevFilter) => ({
          ...prevFilter,
          timestamp: {
            min: newMinTimestamp,
            max: newMaxTimestamp,
          },
        }));
      }
    }
  }, [
    datepickerSettings,
    maxDatepickerTimestamp,
    minDatepickerTimestamp,
    setDatepickerSettings,
    setFilterForRequest,
  ]);

  const getMinDatepickerValue = () => {
    const date = new Date(new Date().setHours(0, 0, 0, 0));
    date.setMonth(date.getMonth() - 24);
    return date;
  };

  const getDatepickerValue = (index: number) => {
    const value =
      !datepickerSettings ||
      !datepickerSettings.timestamp ||
      !datepickerSettings.timestamp.date[index]
        ? ''
        : datepickerSettings.timestamp.date[index];
    if (value) {
      const defaultLanguage = getDefaultLanguage();
      return dayjs(value).locale(getDayjsLocale(defaultLanguage)).format('D.M.YYYY HH:mm');
    }
    return '';
  };

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[],
    customerForSettings: ICustomer[]
  ) => {
    const orderColumns = handleOrderColumns(newOrderColumns, customerForSettings);

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(BLE_USAGE_TABLE_NAME, BluecoinsSettingsSchema);
    if (settings) {
      // setTableSettingsState(settings);
      // setSortFilter({ [`${settings.sort.column}`]: settings.sort.direction });
      handleSetOrderColumns(settings.columns, customer);
      // setIsSettingsReady(true);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['bleUsageCmdTableSettings'],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  return (
    <div>
      <div className="w-page">
        <div className="w-page-content">
          <div className="page-header">
            <h4 className="page-header__title">{f({ id: 'menu.bluecoins' })}</h4>
          </div>
          <div className="container-fluid">
            <TabComponent
              headerStyles={{ marginTop: 0 }}
              tabs={[
                {
                  name: f({ id: 'table_list.tabs.bluecoin.list' }),
                  url: '/bluecoins',
                  isLink: true,
                  dataCy: 'bluecoins-list',
                },
                {
                  name: f({ id: 'table_list.tabs.bluecoin.usage' }),
                  url: '/bluecoins/usage',
                  isLink: true,
                  isActive: true,
                },
              ]}
            />
            <section className="basic-box border-top-thin border-top-gray">
              <div
                className="basic-box__content"
                // style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}
              >
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {/* <TableSettings
                      id="bluecoinUsage"
                      tableName="bluecoins_usage"
                      sort={{ column: 'usageStart', direction: 'desc' }}
                    /> */}
                  </div>
                  <div className="col col-md" />
                  <div className="col-12 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2">
                    {/* {`${f({ id: 'table.results.total' })}:`}
                    <strong>{tableData?.count ? ` ${tableData?.count}` : ` 0`}</strong> */}
                  </div>
                  <div className="col-auto order-4 order-lg-5">
                    {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read']) && <Export />}
                  </div>
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={BLE_USAGE_TABLE_NAME}
                      // actionColumnEnabled={true}
                      tableName={BLE_USAGE_TABLE_SETTINGS_KEY}
                      sort={{ column: 'usageStart', direction: 'desc' }}
                      // translationKeys={TableSettingsKeys}
                      translationKeys={BlucoinsUsageTableTranslationKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  <TableDatepicker
                    name="timestamp"
                    allowedRange={[
                      getMinDatepickerValue(),
                      new Date(new Date().setHours(23, 59, 59, 999)),
                    ]}
                    isActive={true}
                    type="chip"
                    // isDayTime={true}
                    // onClickEvent={handleDayTimeModal}
                  >
                    <div
                      className="label label-primary mr-1 mb-1 label--filter"
                      style={{ cursor: 'pointer', position: 'relative' }}
                      data-cy="datapicker-filter-timestamp"
                    >
                      <span>
                        {`${f({ id: 'costsOverviewForm.dateTime' })}: ${getDatepickerValue(
                          0
                        )} - ${getDatepickerValue(1)}`}
                      </span>
                    </div>
                  </TableDatepicker>
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={BLE_USAGE_TABLE_NAME} />
                    ))}
                </div>
                {isLoadingSettings && <CmdLoader inContent={true} />}
                {filterForRequest?.timestamp && (
                  <CmdTable<BluecoinUsageApiResponse, BluecoinUsageRecord>
                    name={BLE_USAGE_TABLE_NAME}
                    // stickyColumns={[ACTIONS_KEY]}
                    lockedColumns={[
                      USAGE_START_ADDRESS_KEY,
                      USAGE_END_ADDRESS_KEY,
                      VEHICLE_KEY,
                      CUSTOMER_KEY,
                    ]}
                    columns={columns}
                    columnOrder={columnOrder}
                    columnVisibility={columnVisibility}
                    // onSelectedRowsChange={handleRowChange}
                    // jsonSchema={BluecoinsDataSchema}
                    // leftStickyColumn={[CHECKBOX_KEY]}
                  />
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(BluecoinsUsage);
